import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import api from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { money } from "@/components/marketplace/CartDrawer";
import { productImage } from "@/lib/imageUrl";
import { CheckCircle2, Clock, Package, MapPin } from "lucide-react";

export default function OrderConfirmation() {
  const { orderNumber } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [order, setOrder] = useState(undefined);
  const cleared = useRef(false);

  useEffect(() => {
    let active = true;
    let tries = 0;
    let timer;
    const load = async () => {
      try {
        const { data } = await api.get(`/marketplace/orders/${orderNumber}`, { params: sessionId ? { session_id: sessionId } : {} });
        if (!active) return;
        setOrder(data);
        if (data.payment_status === "paid") {
          if (!cleared.current) { cleared.current = true; clear(); }
          return; // settled — stop polling
        }
      } catch {
        if (active) setOrder((o) => (o === undefined ? null : o));
      }
      if (active && tries < 8) { tries += 1; timer = setTimeout(load, 2000); }
    };
    load();
    return () => { active = false; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderNumber, sessionId]);

  if (order === undefined) return <PublicLayout><div className="max-w-xl mx-auto px-4 py-20 text-center text-slate-400" data-testid="order-loading">Loading your order…</div></PublicLayout>;
  if (order === null) return <PublicLayout><div className="max-w-xl mx-auto px-4 py-20 text-center"><p className="font-display text-2xl uppercase">Order not found</p><Link to="/marketplace" className="text-[#E63946] text-sm">Back to Marketplace</Link></div></PublicLayout>;

  const paid = order.payment_status === "paid";
  return (
    <PublicLayout>
      <div className="max-w-2xl mx-auto px-4 py-10" data-testid="order-confirmation">
        <div className="text-center">
          {paid
            ? <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-500" data-testid="order-paid-icon" />
            : <Clock className="w-14 h-14 mx-auto text-amber-500" data-testid="order-pending-icon" />}
          <h1 className="font-display text-3xl uppercase tracking-tight mt-3">{paid ? "Order Confirmed" : "Finalizing Payment…"}</h1>
          <p className="text-slate-500 mt-1">Order <span className="font-data text-[#0A192F]" data-testid="order-number">{order.order_number}</span></p>
          {!paid && <p className="text-sm text-amber-600 mt-2">We're confirming your payment. This page will update automatically.</p>}
          {paid && <p className="text-sm text-slate-500 mt-2">A confirmation email is on its way to {order.email}.</p>}
        </div>

        <div className="mt-8 bg-white border border-slate-200 rounded-sm p-5">
          <div className="divide-y divide-slate-100">
            {order.items.map((l) => (
              <div key={`${l.product_id}::${l.variant_id}`} className="flex gap-3 py-3" data-testid={`order-line-${l.slug}`}>
                <div className="w-14 h-14 shrink-0 bg-slate-50 rounded-sm overflow-hidden border border-slate-200">
                  <img src={productImage(l.image_url)} alt={l.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[#0A192F]">{l.name}</div>
                  <div className="text-xs text-slate-500">{Object.values(l.option_values || {}).join(" · ")} · Qty {l.qty}</div>
                </div>
                <div className="font-data text-sm">{money(l.line_total_cents)}</div>
              </div>
            ))}
          </div>
          <div className="border-t border-slate-200 mt-3 pt-3 space-y-1.5 text-sm">
            <Row label="Subtotal" value={money(order.subtotal_cents)} />
            {order.discount_cents > 0 && <Row label={`Discount${order.discount?.code ? ` (${order.discount.code})` : ""}`} value={`− ${money(order.discount_cents)}`} tone="text-emerald-600" />}
            <Row label={`Shipping${order.shipping?.label ? ` · ${order.shipping.label}` : ""}`} value={order.shipping_cents ? money(order.shipping_cents) : "Free"} />
            {order.tax_cents > 0 && <Row label="Tax" value={money(order.tax_cents)} />}
            <div className="flex justify-between pt-2 border-t border-slate-200 font-semibold text-[#0A192F]"><span>Total</span><span data-testid="order-total" className="font-data text-lg">{money(order.total_cents)}</span></div>
          </div>
          {order.shipping_address?.line1 && (
            <div className="mt-4 pt-3 border-t border-slate-100 text-sm text-slate-600">
              <div className="flex items-center gap-1.5 font-medium text-[#0A192F] mb-1"><MapPin className="w-4 h-4" /> Shipping to</div>
              {order.shipping_address.name && <div>{order.shipping_address.name}</div>}
              <div>{order.shipping_address.line1}{order.shipping_address.line2 ? `, ${order.shipping_address.line2}` : ""}</div>
              <div>{[order.shipping_address.city, order.shipping_address.region, order.shipping_address.postal_code].filter(Boolean).join(", ")}</div>
            </div>
          )}
        </div>

        <div className="text-center mt-6">
          <Link to="/marketplace/products" className="text-[#E63946] text-sm font-semibold">Continue shopping →</Link>
        </div>
      </div>
    </PublicLayout>
  );
}

function Row({ label, value, tone = "text-slate-600" }) {
  return <div className="flex justify-between"><span className={tone}>{label}</span><span className={`font-data ${tone}`}>{value}</span></div>;
}
